import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import { safeLocalStorage } from '@/shared/lib'
import { readColorToken, syncChromeColors, type ChromeColors } from '@/shared/telegram'

/** `system` — палитра клиента Telegram; `light`/`dark` — ручной выбор. */
export type ThemeMode = 'system' | 'light' | 'dark'

type ThemeState = {
  mode: ThemeMode
  setMode: (mode: ThemeMode) => void
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set) => ({
      mode: 'system',
      setMode: (mode) => {
        set({ mode })
        applyThemeAttribute(mode)
        syncThemeChrome()
      },
    }),
    { name: 'tapgift.theme', storage: createJSONStorage(() => safeLocalStorage) },
  ),
)

/**
 * Цвета ручной темы — из CSS: фиксированные палитры живут в app/styles, а не
 * здесь. Читаются любой записью цвета: сборка минифицирует `#ffffff` в `#fff`,
 * и строгая проверка `#rrggbb` в проде молча возвращала палитру клиента —
 * экран светлый, а шапка, панель и MainButton тёмные.
 */
const readManualColors = (): ChromeColors | null => {
  const header = readColorToken('--tg-header-bg')
  const background = readColorToken('--tg-secondary-bg')
  const bottomBar = readColorToken('--tg-bg')
  const button = readColorToken('--tg-button')
  const buttonText = readColorToken('--tg-button-text')

  return header && background && bottomBar && button && buttonText
    ? { header, background, bottomBar, button, buttonText }
    : null
}

const applyThemeAttribute = (mode: ThemeMode): void => {
  const root = document.documentElement

  if (mode === 'system') {
    root.removeAttribute('data-theme')
  } else {
    root.setAttribute('data-theme', mode)
  }
}

/**
 * Применить сохранённую тему. Вызывается в `app/index.tsx` **до первого рендера**:
 * иначе светлая тема начиналась бы со вспышки тёмной.
 *
 * Только палитра страницы, без шапки Telegram: SDK здесь ещё не
 * инициализирован, а обращение к `miniApp` до подмены окружения в dev
 * навсегда запоминает «мы не в Mini App» — и `miniApp.ready`, цвет шапки и
 * `close` перестают работать. Шапку красит `syncThemeChrome()` после `initTelegram()`.
 */
export const applyStoredTheme = (): void => applyThemeAttribute(useThemeStore.getState().mode)

/** Перекрасить шапку и MainButton Telegram под текущую тему — после инициализации SDK. */
export const syncThemeChrome = (): void => {
  const { mode } = useThemeStore.getState()

  syncChromeColors(mode === 'system' ? 'system' : (readManualColors() ?? 'system'))
}
